import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, BookOpen, Edit, Trash2, Eye, EyeOff, Copy, Users } from 'lucide-react';
import toast from 'react-hot-toast';

const ManualDashboard = () => {
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchManuals();
  }, []);

  const fetchManuals = async () => {
    try {
      const response = await axios.get('/api/manual');
      console.log('📋 Загружены мануалы:', response.data);
      setManuals(response.data);
    } catch (error) {
      console.error('Ошибка загрузки мануалов:', error);
      toast.error('Ошибка загрузки мануалов');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот мануал?')) {
      return;
    }

    try {
      await axios.delete(`/api/manual/${id}`);
      toast.success('Мануал удален');
      fetchManuals();
    } catch (error) {
      console.error('Ошибка удаления мануала:', error);
      toast.error('Ошибка удаления мануала');
    }
  };

  const togglePublic = async (id, isPublic) => {
    try {
      console.log('🔍 Переключаем публичность мануала:', { id, isPublic, newValue: !isPublic });
      const response = await axios.put(`/api/manual/${id}`, { isPublic: !isPublic });
      console.log('✅ Мануал обновлен:', response.data);
      toast.success(`Мануал ${!isPublic ? 'опубликован' : 'скрыт'}`);
      fetchManuals();
    } catch (error) {
      console.error('Ошибка изменения статуса:', error);
      toast.error('Ошибка изменения статуса');
    }
  };

  const copyLink = async (slug) => {
    try {
      const url = `${window.location.origin}/manual/${slug}`;
      await navigator.clipboard.writeText(url);
      toast.success('Ссылка скопирована в буфер обмена');
    } catch (error) {
      console.error('Ошибка копирования:', error);
      toast.error('Ошибка копирования ссылки');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Мои Manual</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Создавайте и управляйте мануалами с вложенными структурами</p>
        </div>
        <Link
          to="/manual/new"
          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors flex items-center space-x-2 text-sm"
        >
          <Plus className="h-4 w-4" />
          <span>Создать Manual</span>
        </Link>
      </div>

      {manuals.length === 0 ? (
        <div className="text-center py-8">
          <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">У вас пока нет мануалов</h3>
          <p className="text-sm text-gray-600 mb-4">Создайте свой первый мануал с вложенными структурами</p>
          <Link
            to="/manual/new"
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors inline-flex items-center space-x-2 text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>Создать первый Manual</span>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {manuals.map((manual) => (
            <div key={manual._id} className="bg-white dark:bg-gray-800 rounded-md shadow-sm p-4 hover:shadow-md transition-shadow border border-gray-200 dark:border-gray-700">
              <div className="mb-4">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 line-clamp-2">
                  {manual.title}
                </h3>
              </div>

              {manual.description && (
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3">
                  {manual.description}
                </p>
              )}

              <div className="flex items-center justify-end text-xs text-gray-500 dark:text-gray-400 mb-3">
                <div className="flex items-center space-x-2">
                  {manual.isTeamProject && (
                    <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 flex items-center space-x-1">
                      <Users className="h-3 w-3" />
                      <span>Командный</span>
                    </span>
                  )}
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    manual.isPublic 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {manual.isPublic ? 'Публичный' : 'Приватный'}
                  </span>
                  <button
                    onClick={() => togglePublic(manual._id, manual.isPublic)}
                    className={`p-1 rounded-lg transition-colors ${
                      manual.isPublic 
                        ? 'text-green-600 hover:bg-green-50' 
                        : 'text-gray-400 hover:bg-gray-50'
                    }`}
                    title={manual.isPublic ? 'Скрыть' : 'Опубликовать'}
                  >
                    {manual.isPublic ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <span>Создан: {new Date(manual.createdAt).toLocaleDateString('ru-RU')}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {manual.isPublic && (
                  <Link
                    to={`/manual/${manual.slug}`}
                    className="bg-green-100 text-green-700 px-3 py-1.5 rounded-md hover:bg-green-200 transition-colors text-xs"
                  >
                    Публичный просмотр
                  </Link>
                )}
                <Link
                  to={`/manual/${manual._id}/edit`}
                  className="bg-primary-600 text-white px-3 py-1.5 rounded-md hover:bg-primary-700 transition-colors text-xs"
                >
                  Редактировать
                </Link>
                <button
                  onClick={() => handleDelete(manual._id)}
                  className="bg-red-100 text-red-600 px-3 py-1.5 rounded-md hover:bg-red-200 transition-colors"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>

              {manual.isPublic && (
                <button
                  onClick={() => copyLink(manual.slug)}
                  className="mt-3 bg-orange-100 text-orange-700 px-3 py-1.5 rounded-md hover:bg-orange-200 transition-colors text-xs flex items-center space-x-1"
                >
                  <Copy className="h-3 w-3" />
                  <span>Копировать ссылку</span>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManualDashboard;

